import { Controller } from 'stimulus';
import { sessionExpiredRedirect } from '../utils/session_expired';

export default class extends Controller {
  static targets = ['query', 'results', 'selected', 'count', 'status', 'more', 'retry', 'panel', 'trigger', 'error', 'all', 'picker'];
  static values = { url: String, messages: Object };

  connect() {
    this.sequence = 0;
    this.page = 0;
    this.form = this.element.closest('form');
    this.validateSubmission = this.validate.bind(this);
    this.form?.addEventListener('submit', this.validateSubmission);
    this.updateSelection();
    this.changeScope();
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.validateSubmission);
    clearTimeout(this.timer);
    this.sequence += 1;
    this.request?.abort();
  }

  toggle() {
    if (!this.panelTarget.hidden) return this.close();
    this.panelTarget.hidden = false;
    this.triggerTarget.setAttribute('aria-expanded', 'true');
    this.queryTarget.focus();
  }

  changeScope() {
    const all = this.allTarget.checked;
    this.pickerTarget.hidden = all;
    this.pickerTarget.disabled = all;
    this.close();
    if (!all) {
      if (!this.page) this.load(1);
      return;
    }
    this.errorTarget.hidden = true;
    clearTimeout(this.timer);
    this.sequence += 1;
    this.request?.abort();
  }

  close() {
    this.panelTarget.hidden = true;
    this.triggerTarget.setAttribute('aria-expanded', 'false');
  }

  escape(event) {
    if (event.key !== 'Escape' || this.panelTarget.hidden) return;
    event.preventDefault();
    this.close();
    this.triggerTarget.focus();
  }

  outside(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  validate(event) {
    if (event.target !== this.element.closest('form') || this.allTarget.checked || this.selectedIds().size) return;
    event.preventDefault();
    this.errorTarget.hidden = false;
    event.stopPropagation();
    if (this.panelTarget.hidden) this.toggle();
    this.queryTarget.focus();
  }

  search() {
    clearTimeout(this.timer);
    this.sequence += 1;
    this.request?.abort();
    this.moreTarget.hidden = true;
    this.resultsTarget.replaceChildren();
    this.timer = setTimeout(() => this.load(1), 300);
  }

  searchNow(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    clearTimeout(this.timer);
    this.load(1);
  }

  more() { this.load(this.page + 1); }
  retry() { this.load(this.failedPage || 1); }

  async load(page) {
    this.request?.abort();
    this.request = new AbortController();
    const sequence = ++this.sequence;
    this.retryTarget.hidden = true;
    this.moreTarget.hidden = true;
    this.statusTarget.textContent = this.messagesValue.loading;
    if (page === 1) this.resultsTarget.replaceChildren();
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('q', this.queryTarget.value.trim());
    url.searchParams.set('page', page);

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: this.request.signal,
      });
      if (sequence !== this.sequence) return;
      if (response.redirected) {
        window.location.assign(response.url);
        return;
      }
      if (response.status === 401) {
        // Two 401 shapes reach here: the expired-session contract and this endpoint's own `login_url`.
        const body = await response.json().catch(() => null);
        window.location.assign(sessionExpiredRedirect(response.status, body) || body?.login_url || '/users/sign_in');
        return;
      }
      if (!response.ok) throw new Error('Search failed');
      const body = await response.json();
      if (sequence !== this.sequence) return;
      body.referral_links.forEach((link) => this.addResult(link));
      this.page = page;
      this.moreTarget.hidden = page >= Number(body.meta?.totalPages || 1);
      this.statusTarget.textContent = this.resultsTarget.children.length ? '' : this.messagesValue.empty;
      this.updateSelection();
    } catch (error) {
      if (sequence !== this.sequence || error.name === 'AbortError') return;
      this.failedPage = page;
      this.statusTarget.textContent = this.messagesValue.error;
      this.retryTarget.hidden = false;
    }
  }

  addResult(link) {
    const row = document.createElement('li');
    row.className = 'list-group-item d-flex justify-content-between align-items-center';
    row.dataset.campaignId = String(link.id);
    const label = document.createElement('label');
    label.className = 'd-flex align-items-center mb-0 w-100';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'mr-2';
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) return this.select(link);
      this.unselect(String(link.id));
    });
    const text = document.createElement('span');
    text.textContent = `${link.name} — ${link.slug}${link.enabled ? '' : ` (${this.messagesValue.disabled})`}`;
    label.append(checkbox, text);
    row.append(label);
    this.resultsTarget.append(row);
  }

  select(link) {
    if (this.selectedIds().has(String(link.id))) return;
    const row = document.createElement('li');
    row.className = 'd-inline-flex align-items-center border rounded bg-light px-2 py-1 mr-2 mb-2';
    row.dataset.campaignId = String(link.id);
    const label = document.createElement('span');
    label.textContent = `${link.name} — ${link.slug}`;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'referral_link_report[referral_link_ids][]';
    input.value = link.id;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-sm btn-link ml-1 p-0';
    button.textContent = '×';
    button.setAttribute('aria-label', `${this.messagesValue.remove}: ${link.name} — ${link.slug}`);
    button.dataset.action = 'referral-campaign-selector#remove';
    row.append(label, input, button);
    this.selectedTarget.append(row);
    this.updateSelection();
  }

  remove(event) {
    this.unselect(event.currentTarget.closest('li').dataset.campaignId);
    this.triggerTarget.focus();
  }

  unselect(id) {
    Array.from(this.selectedTarget.children).find((row) => row.dataset.campaignId === id)?.remove();
    this.updateSelection();
  }

  selectedIds() {
    return new Set(Array.from(this.selectedTarget.children, (row) => row.dataset.campaignId));
  }

  updateSelection() {
    const ids = this.selectedIds();
    this.countTarget.textContent = this.messagesValue.selected.replace('%{count}', ids.size);
    if (ids.size) this.errorTarget.hidden = true;
    Array.from(this.resultsTarget.children).forEach((row) => {
      row.querySelector('input').checked = ids.has(row.dataset.campaignId);
    });
  }
}
